import { IconButton, Tooltip } from '@mui/material';
import { CircleHelp } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { DENSITY_OVERRIDE_DECIMALS } from '~/utils/units';

/**
 * Small muted icon shown in its own fixed-width column to the right of the
 * structure name when the ROI has an electron and/or mass density override
 * set in its DICOM ROI Physical Properties Sequence (3006,00B0). Hovering or
 * focusing the icon reveals a tooltip listing the active density values.
 * When no override is set an empty placeholder is rendered so the reserved
 * grid column stays occupied and indicators align vertically across rows.
 */
export default function DensityIndicator({ roi }: { roi: RoiReadModel }) {
    const intl = useIntl();
    const hasElectron = roi.electronDensityOverride != null;
    const hasMass = roi.massDensityOverride != null;
    if (!hasElectron && !hasMass) {
        return <span aria-hidden="true" />;
    }
    return (
        <Tooltip
            title={
                <>
                    {hasElectron && (
                        <div>
                            <FormattedMessage id="structure.electronDensity" />
                            {`: ${roi.electronDensityOverride!.toFixed(
                                DENSITY_OVERRIDE_DECIMALS
                            )}`}
                        </div>
                    )}
                    {hasMass && (
                        <div>
                            <FormattedMessage id="structure.massDensity" />
                            {`: ${roi.massDensityOverride!.toFixed(
                                DENSITY_OVERRIDE_DECIMALS
                            )}`}
                        </div>
                    )}
                </>
            }
            enterDelay={300}
            leaveDelay={200}
            placement="left"
        >
            <IconButton
                size="small"
                aria-label={intl.formatMessage({
                    id: 'structure.densityOverrideInfo',
                })}
                sx={{
                    p: 0,
                    justifySelf: 'center',
                    color: 'text.secondary',
                }}
                data-testid={`structure-density-indicator-${roi.name}`}
            >
                <CircleHelp size={12} />
            </IconButton>
        </Tooltip>
    );
}
